/* eslint-disable react/jsx-props-no-spreading */
import React, { JSX, useCallback, useEffect, useMemo, useRef } from 'react';

import { Pressable, ScrollView, StyleSheet, View } from 'react-native';

import { useTranslation } from 'react-i18next';
import Animated from 'react-native-reanimated';

import CheckMarkImage from 'assets/images/svgs/ico_checkmark.svg';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import { PlayerTrack } from 'store/player/reducer.types';

import setAccessibilityFocusTo from 'services/accessibility/setAccessibilityFocusTo';

import { getScreenSize } from 'utils/responsiveness';

import LocalizedText from 'components/LocalizedText';

import { IProps } from '../types';
import SelectionOption from './components/SelectionOption';
import styles, {
    MARGIN_RIGHT,
    MARGIN_TOP,
    MAX_MENU_WIDTH,
    SVG_HALF_WIDTH,
} from './styles';

const {
    SCREEN_AUDIO_SUBTITLES_SUBTITLES,
    SCREEN_AUDIO_SUBTITLES_AUDIO_LANGUAGE,
    ACCESSIBILITY: {
        PLAYER: { ACCESSIBILITY_PLAYER_CLOSE_AUDIO_AND_SUBTITLE_OVERLAY },
    },
} = LANGUAGE_KEYS;

const Section = ({
    title,
    data,
    renderItem,
}: {
    title: string;
    data: PlayerTrack[];
    renderItem(value: PlayerTrack): JSX.Element;
}) => {
    return (
        <>
            <LocalizedText
                style={styles.titleText}
                textKey={title}
                accessibilityRole="header"
            />
            {!!data.length && data.map(renderItem)}
        </>
    );
};

const SubtitleAudioDropDown: React.FC<IProps> = (props: IProps) => {
    const {
        onTextTrackPress,
        onAudioTrackPress,
        keyExtractor,
        selectedAudioTrack,
        selectedTextTrack,
        onClose,
        isVisible,
        subtitleButtonPosition,
        audioTracks = [],
        textTracks = [],
    } = props;
    const { t } = useTranslation();
    const accessibleViewRef = useRef(null);

    const positionStyle = useMemo(() => {
        return (
            subtitleButtonPosition && {
                top: subtitleButtonPosition.y + MARGIN_TOP,
                left: getScreenSize('width') - MAX_MENU_WIDTH - MARGIN_RIGHT,
            }
        );
    }, [subtitleButtonPosition]);

    const renderAudioItem = useCallback(
        (item: PlayerTrack) => {
            const isSelected = item.id === selectedAudioTrack?.id;

            return (
                <SelectionOption
                    item={item}
                    key={keyExtractor(item)}
                    onPress={onAudioTrackPress}
                    isSelected={isSelected}
                    SvgRightImage={isSelected ? CheckMarkImage : null}
                    isAudio
                />
            );
        },
        [keyExtractor, onAudioTrackPress, selectedAudioTrack?.id],
    );

    const renderTextItem = useCallback(
        (item: PlayerTrack) => {
            const isSelected = item.id === selectedTextTrack?.id;

            return (
                <SelectionOption
                    item={item}
                    key={keyExtractor(item)}
                    onPress={onTextTrackPress}
                    isSelected={isSelected}
                    SvgRightImage={isSelected ? CheckMarkImage : null}
                />
            );
        },
        [keyExtractor, onTextTrackPress, selectedTextTrack?.id],
    );

    const rightPositionForArrow = useMemo(() => {
        return (
            subtitleButtonPosition && {
                right:
                    getScreenSize('width') -
                    subtitleButtonPosition.x -
                    MARGIN_RIGHT -
                    subtitleButtonPosition.width +
                    SVG_HALF_WIDTH,
            }
        );
    }, [subtitleButtonPosition]);

    const audioTrackProps = {
        renderItem: renderAudioItem,
        data: audioTracks,
        title: SCREEN_AUDIO_SUBTITLES_AUDIO_LANGUAGE,
    };

    const textTrackProps = {
        renderItem: renderTextItem,
        data: textTracks,
        title: SCREEN_AUDIO_SUBTITLES_SUBTITLES,
    };

    useEffect(() => {
        if (isVisible) {
            setAccessibilityFocusTo(accessibleViewRef);
        }
    }, [isVisible]);

    if (!isVisible) {
        return null;
    }

    return (
        <View style={StyleSheet.absoluteFill}>
            <Pressable
                style={StyleSheet.absoluteFill}
                accessibilityLabel={t(
                    ACCESSIBILITY_PLAYER_CLOSE_AUDIO_AND_SUBTITLE_OVERLAY,
                )}
                onPress={onClose}
                ref={accessibleViewRef}
            />

            <Animated.View style={[styles.container, positionStyle]}>
                <View style={[styles.blockArrow, rightPositionForArrow]} />
                <ScrollView>
                    {audioTrackProps.data.length > 1 && (
                        <Section {...audioTrackProps} />
                    )}
                    {!!textTrackProps.data.length && (
                        <Section {...textTrackProps} />
                    )}
                </ScrollView>
            </Animated.View>
        </View>
    );
};

export default SubtitleAudioDropDown;
